import {
  PdfGen,
  PdfUnit,
  PdfSize,
  PdfRect,
  PdfPoint,
  PdfBrush,
  PdfColor,
  PageGen,
} from '../../pdf/pdf';
import { ETextAlign } from '../../pdf/pdf';
import { ILayoutImageGen } from '../layoutImage';
import { IItemCount } from '@educraft/base';
import { OptsImage } from '../optsImage';
import { GUtil } from '../../pdf/gutil';
import { calcOrigin } from '../../core/genHelper';
import '../../pdf/draw/primitives';
import '../../pdf/draw/string';
import '../../pdf/draw/image';
import '../../pdf/draw/cut';
import '../../pdf/draw/shapes';

/**
 * Rozvrh_hodin - School timetable generators (3 variants)
 *
 * Each variant defines a list of timetable configurations with different
 * layouts (plain/left image/right image), grid sizes, and styling options.
 * Pages are generated dynamically based on the configuration list.
 *
 * Migrated from C# EduFrame.Img10.Generators.TimetableBase + Rozvrh_hodin__1/2/3
 */

interface TimetableDef {
  sizeX: number;
  sizeY: number;
  rotate: boolean;
  hours: string[];
  action: (
    page: PageGen,
    rect: PdfRect,
    brush: PdfBrush,
    def: TimetableDef,
    opts: OptsImage,
    days: string[]
  ) => void;
  hasOutline: boolean;
  isRound: boolean;
  shadowHeader: boolean;
}

const allColors = [
  ['#96a2fa', '#a7aff2', '#b9bce9', '#cbc9e0', '#ded5d5', '#f0e0c9'],
  ['#7cbfc3', '#93c6b1', '#a8cc9d', '#bdd286', '#d1d769', '#e0d258'],
  ['#4b9323', '#60a238', '#74b14b', '#88c15d', '#9dd06e', '#b1e080'],
  ['#e8e7db', '#e5d8ea', '#e2c7f8', '#dcb8ff', '#d4a9ff', '#cd99ff'],
];

function drawTable(page: PageGen, rect: PdfRect, def: TimetableDef, days: string[]): void {
  const margin = PdfUnit.FromMillimeters(0.5);
  const roundRadius = PdfUnit.FromMillimeters(4);

  const background = PdfBrush.White;
  const header = def.shadowHeader
    ? new PdfBrush(PdfColor.fromCmyk(0.5, 0, 0, 0, 0))
    : PdfBrush.White;

  const xc = def.hours.length + 1;
  const yc = days.length + 1;

  const cellW = rect.width / xc;
  const cellH = rect.height / yc;

  for (let x = 0; x < xc; x++) {
    for (let y = 0; y < yc; y++) {
      const r = new PdfRect(rect.x + x * cellW, rect.y + y * cellH, cellW, cellH);
      const r1 = new PdfRect(
        r.x + margin,
        r.y + margin,
        r.width - 2 * margin,
        r.height - 2 * margin
      );

      if (x === 0 && y === 0) {
        // top-left corner: empty
      } else if (x === 0) {
        if (def.isRound) {
          page.drawRoundedRectangle(null, header, r1, roundRadius);
          page.drawString(days[y - 1], GUtil.baseFont, PdfBrush.Black, null, r1, ETextAlign.Center);
          if (def.hasOutline) page.drawRoundedRectangle(GUtil.basePen, null, r1, roundRadius);
        } else {
          page.drawRectangle(null, header, r);
          page.drawString(days[y - 1], GUtil.baseFont, PdfBrush.Black, null, r, ETextAlign.Center);
          if (def.hasOutline) page.drawRectangle(GUtil.basePen, null, r);
        }
      } else if (y === 0) {
        if (def.isRound) {
          page.drawRoundedRectangle(null, header, r1, roundRadius);
          page.drawString(
            def.hours[x - 1],
            GUtil.baseFont,
            PdfBrush.Black,
            null,
            r1,
            ETextAlign.Center
          );
          if (def.hasOutline) page.drawRoundedRectangle(GUtil.basePen, null, r1, roundRadius);
        } else {
          page.drawRectangle(null, header, r);
          page.drawString(
            def.hours[x - 1],
            GUtil.baseFont,
            PdfBrush.Black,
            null,
            r,
            ETextAlign.Center
          );
          if (def.hasOutline) page.drawRectangle(GUtil.basePen, null, r);
        }
      } else {
        if (def.isRound) {
          page.drawRoundedRectangle(null, background, r1, roundRadius);
          if (def.hasOutline) page.drawRoundedRectangle(GUtil.basePen, null, r1, roundRadius);
        } else {
          page.drawRectangle(null, background, r);
          if (def.hasOutline) page.drawRectangle(GUtil.basePen, null, r);
        }
      }
    }
  }
}

function drawPlain(
  page: PageGen,
  rect: PdfRect,
  brush: PdfBrush,
  def: TimetableDef,
  _opts: OptsImage,
  days: string[]
): void {
  const margin = PdfUnit.FromMillimeters(10);
  const r = new PdfRect(
    rect.x + margin,
    rect.y + margin,
    rect.width - 2 * margin,
    rect.height - 2 * margin
  );

  page.drawRectangle(null, brush, rect);
  drawTable(page, r, def, days);
  page.drawRectangle(GUtil.cutPen, null, rect);
}

function drawLeft(
  page: PageGen,
  rect: PdfRect,
  brush: PdfBrush,
  def: TimetableDef,
  opts: OptsImage,
  days: string[]
): void {
  const margin = PdfUnit.FromMillimeters(10);
  const rx = new PdfRect(
    rect.x + margin,
    rect.y + margin,
    rect.width - 2 * margin,
    rect.height - 2 * margin
  );

  const r0 = new PdfRect(rx.x, rx.y, rx.width / 3 - margin, rx.height);
  const r1 = new PdfRect(rx.x + r0.width + margin, rx.y, rx.width - r0.width - margin, rx.height);

  page.drawRectangle(null, brush, rect);

  const imgIndex = Math.floor(Math.random() * opts.list.length);
  if (imgIndex < opts.list.length) {
    page.drawImageCenter(opts.list[imgIndex].image, r0);
  }

  drawTable(page, r1, def, days);
  page.drawRectangle(GUtil.cutPen, null, rect);
}

function drawRight(
  page: PageGen,
  rect: PdfRect,
  brush: PdfBrush,
  def: TimetableDef,
  opts: OptsImage,
  days: string[]
): void {
  const margin = PdfUnit.FromMillimeters(10);
  const rx = new PdfRect(
    rect.x + margin,
    rect.y + margin,
    rect.width - 2 * margin,
    rect.height - 2 * margin
  );

  const r1 = new PdfRect(rx.x, rx.y, (rx.width * 2) / 3 - margin, rx.height);
  const r0 = new PdfRect(rx.x + r1.width + margin, rx.y, rx.width - r1.width - margin, rx.height);

  page.drawRectangle(null, brush, rect);

  const imgIndex = Math.floor(Math.random() * opts.list.length);
  if (imgIndex < opts.list.length) {
    page.drawImageCenter(opts.list[imgIndex].image, r0);
  }

  drawTable(page, r1, def, days);
  page.drawRectangle(GUtil.cutPen, null, rect);
}

function generateTimetable(list: TimetableDef[]) {
  return async (pdf: PdfGen, opts: OptsImage): Promise<void> => {
    const colors = GUtil.randomBrushes(opts.data.random, allColors);
    let colorIndex = 0;

    const {lang} = opts.data;
    const localizedDays = [
      lang.Day_Short_Mon ?? 'Mon',
      lang.Day_Short_Tue ?? 'Tue',
      lang.Day_Short_Wed ?? 'Wed',
      lang.Day_Short_Thu ?? 'Thu',
      lang.Day_Short_Fri ?? 'Fri',
    ];

    for (const item of list) {
      const page = pdf.addPage();
      const { origin, size } = calcOrigin(item.sizeX, item.sizeY, 0, PdfSize.A4);

      for (let x = 0; x < item.sizeX; x++) {
        for (let y = 0; y < item.sizeY; y++) {
          if (item.rotate) {
            const rect = new PdfRect(
              origin.x + x * size.width,
              origin.y + y * size.height,
              size.width,
              size.height
            );
            const rr = new PdfRect(
              rect.centerX - rect.height / 2,
              rect.centerY - rect.width / 2,
              rect.height,
              rect.width
            );

            page.save();
            page.rotateAtTransform(90, new PdfPoint(rect.centerX, rect.centerY));
            item.action(page, rr, colors[colorIndex++ % colors.length], item, opts, localizedDays);
            page.restore();
          } else {
            const rect = new PdfRect(
              origin.x + x * size.width,
              origin.y + y * size.height,
              size.width,
              size.height
            );
            item.action(
              page,
              rect,
              colors[colorIndex++ % colors.length],
              item,
              opts,
              localizedDays
            );
          }
        }
      }
    }
  };
}

const hours4 = ['1.', '2.', '3.', '4.'];
const hours6 = ['1.', '2.', '3.', '4.', '5.', '6.'];
const hours8 = ['1.', '2.', '3.', '4.', '5.', '6.', '7.', '8.'];
const hours9 = ['0', '1', '2', '3', '4', '5', '6', '7', '8'];

export class RozvrhHodin__1 implements ILayoutImageGen, IItemCount {
  countDefault(): number {
    return 10;
  }
  countMin(): number {
    return 10;
  }
  countMax(): number {
    return 10;
  }
  public generate = generateTimetable([
    {
      sizeX: 1,
      sizeY: 4,
      rotate: false,
      hours: hours6,
      action: drawRight,
      hasOutline: false,
      isRound: true,
      shadowHeader: true,
    },
    {
      sizeX: 1,
      sizeY: 4,
      rotate: false,
      hours: hours4,
      action: drawLeft,
      hasOutline: true,
      isRound: false,
      shadowHeader: false,
    },
    {
      sizeX: 1,
      sizeY: 4,
      rotate: false,
      hours: hours8,
      action: drawPlain,
      hasOutline: true,
      isRound: false,
      shadowHeader: false,
    },
    {
      sizeX: 3,
      sizeY: 2,
      rotate: true,
      hours: hours4,
      action: drawRight,
      hasOutline: false,
      isRound: true,
      shadowHeader: true,
    },
    {
      sizeX: 3,
      sizeY: 2,
      rotate: true,
      hours: hours6,
      action: drawLeft,
      hasOutline: true,
      isRound: false,
      shadowHeader: true,
    },
    {
      sizeX: 3,
      sizeY: 2,
      rotate: true,
      hours: hours8,
      action: drawPlain,
      hasOutline: true,
      isRound: false,
      shadowHeader: true,
    },
    {
      sizeX: 2,
      sizeY: 4,
      rotate: false,
      hours: hours4,
      action: drawPlain,
      hasOutline: false,
      isRound: false,
      shadowHeader: true,
    },
    {
      sizeX: 1,
      sizeY: 4,
      rotate: false,
      hours: hours9,
      action: drawPlain,
      hasOutline: false,
      isRound: true,
      shadowHeader: true,
    },
  ]);
}

export class RozvrhHodin__2 implements ILayoutImageGen, IItemCount {
  countDefault(): number {
    return 10;
  }
  countMin(): number {
    return 10;
  }
  countMax(): number {
    return 10;
  }
  public generate = generateTimetable([
    {
      sizeX: 1,
      sizeY: 4,
      rotate: false,
      hours: hours4,
      action: drawLeft,
      hasOutline: false,
      isRound: true,
      shadowHeader: true,
    },
    {
      sizeX: 3,
      sizeY: 2,
      rotate: true,
      hours: hours6,
      action: drawLeft,
      hasOutline: false,
      isRound: true,
      shadowHeader: true,
    },
    {
      sizeX: 1,
      sizeY: 4,
      rotate: false,
      hours: hours4,
      action: drawRight,
      hasOutline: true,
      isRound: false,
      shadowHeader: true,
    },
    {
      sizeX: 3,
      sizeY: 2,
      rotate: true,
      hours: hours6,
      action: drawRight,
      hasOutline: true,
      isRound: false,
      shadowHeader: true,
    },
    {
      sizeX: 3,
      sizeY: 2,
      rotate: true,
      hours: hours8,
      action: drawRight,
      hasOutline: true,
      isRound: false,
      shadowHeader: true,
    },
  ]);
}

export class Rozvrhhodin__3 implements ILayoutImageGen, IItemCount {
  countDefault(): number {
    return 10;
  }
  countMin(): number {
    return 10;
  }
  countMax(): number {
    return 10;
  }
  public generate = generateTimetable([
    {
      sizeX: 1,
      sizeY: 4,
      rotate: false,
      hours: hours6,
      action: drawLeft,
      hasOutline: false,
      isRound: true,
      shadowHeader: true,
    },
    {
      sizeX: 3,
      sizeY: 2,
      rotate: true,
      hours: hours6,
      action: drawLeft,
      hasOutline: false,
      isRound: true,
      shadowHeader: true,
    },
    {
      sizeX: 3,
      sizeY: 2,
      rotate: true,
      hours: hours6,
      action: drawRight,
      hasOutline: true,
      isRound: false,
      shadowHeader: true,
    },
  ]);
}
